// Escutas globais de telemetria — montadas UMA vez no RootLayout (fica vivo entre
// rotas). Cada bloco é independente e usa só APIs do browser; nada de PII.
//   • page_view  — a cada troca de rota da SPA (o gtag não reconta sozinho)
//   • page_manual_refresh — F5/recarregar (Navigation Timing)
//   • rage_click — 3+ cliques no mesmo elemento em <2s (UI travada)
//   • visibility_hidden_duration — tempo com a aba escondida
//   • app_multi_tab — mesmo vendedor com 2+ abas do Lai abertas
//   • identify — amarra a sessão à pessoa no PostHog (só o id, nada de PII)
import { useEffect } from "react";
import { useRouterState } from "@tanstack/react-router";
import { useSession } from "./auth-client.js";
import { identifyUser, track, trackPageView } from "./analytics.js";
import { useConsent } from "./consent.js";

// Janela e limiar do "clique de raiva": 3 cliques no MESMO elemento em até 2s.
const RAGE_CLICK_THRESHOLD = 3;
const RAGE_CLICK_WINDOW_MS = 2000;
// Só reporta aba escondida se passou de 1s (ignora Alt+Tab relâmpago).
const HIDDEN_MIN_MS = 1000;

// Descreve o elemento clicado SEM vazar texto (que pode conter PII): tag + id +
// 1ª classe + data-testid, subindo até o botão/link mais próximo se for um ícone.
function describeElement(el: Element): string {
  const target = el.closest("button, a, [role='button'], input, select, textarea") ?? el;
  const tag = target.tagName.toLowerCase();
  const id = target.id ? `#${target.id}` : "";
  const testid = target.getAttribute("data-testid");
  const cls =
    typeof target.className === "string" && target.className
      ? `.${target.className.trim().split(/\s+/)[0]}`
      : "";
  return `${tag}${id}${testid ? `[${testid}]` : ""}${cls}`.slice(0, 100);
}

export function AnalyticsListeners(): null {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { data: session, isPending } = useSession();
  const userId = session?.user?.id ?? null;
  // Sem aceite, track() e trackPageView() já não mandam nada. A dependência
  // serve ao aceite tardio: a página em que a pessoa aceitou conta como visita.
  const granted = useConsent().consent?.analytics === "granted";

  // page_view a cada rota (inclui a carga inicial: o config manda send_page_view:false).
  useEffect(() => {
    trackPageView(pathname);
  }, [pathname, granted]);

  // Identificar de UM lugar só cobre todos os caminhos de entrada — login,
  // cadastro, convite aceito e reload com sessão viva — em vez de depender de
  // cada tela lembrar de chamar.
  //
  // Nada acontece enquanto a sessão carrega nem para quem está deslogado: o
  // `reset` do PostHog só é chamado no logout (lib/auth-client.ts). Chamá-lo
  // aqui a cada render sem sessão trocaria o distinct_id anônimo o tempo todo e
  // quebraria o funil de quem ainda vai entrar.
  useEffect(() => {
    if (isPending || !userId || !granted) return;
    identifyUser(userId);
  }, [isPending, userId, granted]);

  // page_manual_refresh — dispara uma vez se a página veio de um reload (F5).
  useEffect(() => {
    const [nav] = performance.getEntriesByType("navigation") as PerformanceNavigationTiming[];
    if (nav?.type === "reload") {
      track("page_manual_refresh", { path: window.location.pathname });
    }
  }, []);

  // rage_click — cliques repetidos no mesmo alvo dentro da janela.
  useEffect(() => {
    let lastTarget: Element | null = null;
    let count = 0;
    let resetTimer: ReturnType<typeof setTimeout> | undefined;
    const onClick = (e: MouseEvent) => {
      const el = e.target as Element | null;
      if (!el) return;
      if (el === lastTarget) count += 1;
      else {
        lastTarget = el;
        count = 1;
      }
      clearTimeout(resetTimer);
      resetTimer = setTimeout(() => {
        lastTarget = null;
        count = 0;
      }, RAGE_CLICK_WINDOW_MS);
      if (count >= RAGE_CLICK_THRESHOLD) {
        track("rage_click", { element: describeElement(el), clicks: count });
        lastTarget = null;
        count = 0;
      }
    };
    document.addEventListener("click", onClick, true);
    return () => {
      document.removeEventListener("click", onClick, true);
      clearTimeout(resetTimer);
    };
  }, []);

  // visibility_hidden_duration — cronometra cada período com a aba oculta.
  useEffect(() => {
    let hiddenAt: number | null = null;
    const onVisibility = () => {
      if (document.visibilityState === "hidden") {
        hiddenAt = performance.now();
      } else if (hiddenAt != null) {
        const duration_ms = Math.round(performance.now() - hiddenAt);
        hiddenAt = null;
        if (duration_ms >= HIDDEN_MIN_MS) track("visibility_hidden_duration", { duration_ms });
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, []);

  // app_multi_tab — descobre outras abas do Lai via BroadcastChannel e reporta o
  // pico de abas simultâneas (só quando bate um novo máximo, para não spammar).
  useEffect(() => {
    if (typeof BroadcastChannel === "undefined") return;
    const channel = new BroadcastChannel("lai-disk-tabs");
    const myId = crypto.randomUUID();
    const peers = new Set<string>();
    let reportedPeak = 1;

    const reportPeak = () => {
      const tabCount = peers.size + 1;
      if (tabCount > reportedPeak) {
        reportedPeak = tabCount;
        track("app_multi_tab", { tab_count: tabCount });
      }
    };
    channel.onmessage = (e: MessageEvent) => {
      const { type, id } = (e.data ?? {}) as { type?: string; id?: string };
      if (!id || id === myId) return;
      if (type === "hello") {
        peers.add(id);
        channel.postMessage({ type: "here", id: myId }); // responde para a aba nova nos contar
        reportPeak();
      } else if (type === "here") {
        peers.add(id);
        reportPeak();
      } else if (type === "bye") {
        peers.delete(id);
      }
    };
    channel.postMessage({ type: "hello", id: myId });
    return () => {
      channel.postMessage({ type: "bye", id: myId });
      channel.close();
    };
  }, []);

  return null;
}
